import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from "@tanstack/react-query";
import { z } from "zod";
import { requestApi, requestApiJson } from "../../api-client";
import {
  EMPTY_PLANNER_FILTERS,
  PlaceSchema,
  RaceSchema,
  ResultSchema,
  type PlannerFilters,
  type PlannerLocale,
  type PlannerRace,
  type PlannerSort,
} from "./planner-contracts";

export type PlannerRacePage = {
  readonly races: readonly PlannerRace[];
  readonly nextCursor: string | null;
  readonly totalCount: number;
};

const plannerPlacesQueryKey = ["planner", "places"] as const;
const plannerRacesQueryRoot = ["planner", "races"] as const;

export function plannerRaceQueryKey(
  savedView: boolean,
  filters: PlannerFilters,
  search: string,
  sort: PlannerSort,
) {
  return [
    ...plannerRacesQueryRoot,
    savedView ? "saved" : "public",
    savedView ? null : filters,
    search,
    sort,
  ] as const;
}

function plannerPlacesQueryOptions() {
  return {
    queryKey: plannerPlacesQueryKey,
    queryFn: () => requestApiJson("/api/v1/planner/places", PlaceSchema),
    staleTime: 5 * 60_000,
  } as const;
}

async function loadPlannerRacePage(
  filters: PlannerFilters,
  savedView: boolean,
  cursor: string | null,
  search: string,
  sort: PlannerSort,
): Promise<PlannerRacePage> {
  const params = new URLSearchParams({ sort });
  if (search !== "") params.set("search", search);
  if (savedView) {
    const races = await requestApiJson(
      `/api/v1/planner/saved-races?${params.toString()}`,
      z.array(RaceSchema),
      { credentials: "include" },
    );
    return {
      races,
      nextCursor: null,
      totalCount: races.length,
    };
  }

  for (const [key, value] of Object.entries(filters)) {
    if (value !== "") params.set(key, value);
  }
  if (cursor !== null) params.set("cursor", cursor);
  return requestApiJson(
    `/api/v1/planner/races?${params.toString()}`,
    ResultSchema,
    { credentials: "include" },
  );
}

export function usePlannerQueries({
  filters,
  savedView,
  savedViewEnabled,
  search,
  sort,
}: {
  readonly filters: PlannerFilters | null;
  readonly savedView: boolean;
  readonly savedViewEnabled: boolean;
  readonly search: string;
  readonly sort: PlannerSort;
}) {
  const places = useQuery(plannerPlacesQueryOptions());
  const defaultFilters: PlannerFilters = {
    ...EMPTY_PLANNER_FILTERS,
    season: places.data?.filters.seasons[0] ?? "",
  };
  const queryFilters = filters ?? defaultFilters;
  const races = useInfiniteQuery({
    queryKey: plannerRaceQueryKey(savedView, queryFilters, search, sort),
    queryFn: ({ pageParam }) =>
      loadPlannerRacePage(queryFilters, savedView, pageParam, search, sort),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    enabled: places.isSuccess && (!savedView || savedViewEnabled),
  });

  return { defaultFilters, places, queryFilters, races };
}

type SaveRaceVariables = {
  readonly raceId: string;
  readonly saved: boolean;
};

type SaveRaceContext = {
  readonly previous: readonly (readonly [
    QueryKey,
    InfiniteData<PlannerRacePage, string | null> | undefined,
  ])[];
};

export function useSavePlannerRaceMutation() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, SaveRaceVariables, SaveRaceContext>({
    mutationFn: async ({ raceId, saved }) => {
      await requestApi(`/api/v1/planner/saved-races/${raceId}`, {
        method: saved ? "PUT" : "DELETE",
        headers: { "content-type": "application/json" },
      });
    },
    onMutate: async ({ raceId, saved }) => {
      await queryClient.cancelQueries({ queryKey: plannerRacesQueryRoot });
      const previous = queryClient.getQueriesData<
        InfiniteData<PlannerRacePage, string | null>
      >({ queryKey: plannerRacesQueryRoot });
      queryClient.setQueriesData<InfiniteData<PlannerRacePage, string | null>>(
        { queryKey: plannerRacesQueryRoot },
        (data) =>
          mapPlannerRaces(data, (race) =>
            race.id === raceId ? { ...race, saved } : race,
          ),
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      for (const [queryKey, data] of context?.previous ?? []) {
        queryClient.setQueryData(queryKey, data);
      }
    },
    onSuccess: (_data, { raceId, saved }) => {
      if (!saved) {
        queryClient.setQueriesData<
          InfiniteData<PlannerRacePage, string | null>
        >({ queryKey: [...plannerRacesQueryRoot, "saved"] }, (data) =>
          mapPlannerRaces(data, (race) => race, raceId),
        );
      }
      void queryClient.invalidateQueries({
        queryKey: [...plannerRacesQueryRoot, "saved"],
        refetchType: "inactive",
      });
    },
  });
}

function mapPlannerRaces(
  data: InfiniteData<PlannerRacePage, string | null> | undefined,
  mapRace: (race: PlannerRace) => PlannerRace,
  removeRaceId?: string,
): InfiniteData<PlannerRacePage, string | null> | undefined {
  if (data === undefined) return undefined;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      races: page.races.filter((race) => race.id !== removeRaceId).map(mapRace),
      totalCount:
        removeRaceId === undefined
          ? page.totalCount
          : Math.max(0, page.totalCount - 1),
    })),
  };
}

export function usePlannerExportMutation() {
  return useMutation({
    mutationFn: async ({
      races,
      filters,
      locale,
    }: {
      readonly races: readonly PlannerRace[];
      readonly filters: PlannerFilters;
      readonly locale: PlannerLocale;
    }) => {
      const { createPlannerPdf } = await import("./planner-pdf");
      return createPlannerPdf({
        races: races.slice(0, 100),
        filters,
        locale,
        generatedAt: new Date(),
      });
    },
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    },
  });
}
